import { useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import { Box, Typography, Stack } from '@mui/material/';
import {
  ControlBar,
  IControlButton,
  TableComponent,
  MainContainer,
  KpiBadge,
  useNotification,
  IColumn,
} from '../../components';
import URLs from '../URLs';
import { CheckboxWrapper } from './KpiResults.styles';
import { Checkbox } from '@mui/material';
import { useSalonStore } from '../../store';
import {
  BenchmarkItemValues,
  KpiItemValues,
  KpiStatus,
  KpiTitle,
  SalonActionPlan,
} from '../../types';
import { KpiColumns, KpiValues, SalonKpiValues, statusOrder } from './types';
import { useTranslation } from 'react-i18next';

const KpiResults = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showMessage } = useNotification();
  const { salonId, planId, inputId, year, quarter } = useParams();
  const maxRowsSelected = 3;
  const {
    salonKpiData,
    fetchSalonKpiData,
    updateSelectedKpi,
    salonSelectedKpi,
    updateActionPlan,
    getActionPlan,
    actionPlan,
  } = useSalonStore();

  const [isMinSelected, setIsMinSelected] = useState(salonSelectedKpi.length > 0);

  const prepareRecommendedKpi = (kpi: string[]) =>
    kpi?.filter((kpiName) => salonKpiData?.kpi[kpiName].value !== null) || [];

  useEffect(() => {
    fetchSalonKpiData(salonId, inputId, year, quarter, showMessage, navigate);
    updateSelectedKpi(prepareRecommendedKpi(salonKpiData?.globalBenchmark?.kpiRecommendations));
    planId && getActionPlan(planId, showMessage, navigate);
    scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const kpi = actionPlan
      ? actionPlan &&
      prepareRecommendedKpi([
        ...Object.keys(actionPlan.guestPlan.kpi),
        ...Object.keys(actionPlan.teamPlan.kpi),
        ...Object.keys(actionPlan.higherTicketsPlan.kpi),
      ])
      : prepareRecommendedKpi(salonKpiData?.globalBenchmark?.kpiRecommendations);
    updateSelectedKpi(kpi);
  }, [salonKpiData?.globalBenchmark?.kpiRecommendations, actionPlan]);

  const isIntlBenchmark: boolean = useMemo(() => {
    if (salonKpiData === null) return false;
    return salonKpiData.benchmarkData?.benchmarkType === 'international_benchmark';
  }, [salonKpiData]);

  useEffect(() => {
    setIsMinSelected(salonSelectedKpi.length > 0);
  }, [salonSelectedKpi]);

  const checkboxHandler = (id: string, isMaxSelected: boolean): void => {
    if (salonKpiData === null) return;

    if (salonSelectedKpi.includes(id)) {
      updateSelectedKpi(salonSelectedKpi.filter((kpi: string) => kpi !== id));
    } else {
      !isMaxSelected && updateSelectedKpi([...salonSelectedKpi, id]);
    }
  };

  const handleBackButton = () => {
    navigate(
      generatePath(`${URLs.Salon}/${URLs.NewPlan}`, {
        salonId,
        year,
        quarter,
        planId: planId || null,
      }),
    );
  };

  const handleNextButton = () => {

    const base = actionPlan || {
      salonDetailPage: salonId,
      salonKpi: salonKpiData?.id,
      year: year,
      quarter: quarter,
      guestPlan: {
        kpi: [],
        plannedGuestCount: { m1: null, m2: null, m3: null },
      },
      teamPlan: {
        kpi: [],
        plannedSpCount: { m1: null, m2: null, m3: null },
      },
      higherTicketsPlan: {
        kpi: [],
        plannedHigherTicketsCount: { m1: null, m2: null, m3: null },
        plannedRetailTicketsCount: { m1: null, m2: null, m3: null },
      },
      initiatives: [],
    };

    const payload: SalonActionPlan = {
      ...base,
      guestPlan: {
        ...base.guestPlan,
        kpi: salonSelectedKpi.filter((item) => salonKpiData.kpi[item].planType === 'guest_plan'),
      },
      teamPlan: {
        ...base.teamPlan,
        kpi: salonSelectedKpi.filter((item) => salonKpiData.kpi[item].planType === 'team_plan'),
      },
      higherTicketsPlan: {
        ...base.higherTicketsPlan,
        kpi: salonSelectedKpi.filter((item) => salonKpiData.kpi[item].planType === 'higher_tickets_plan'),
      },
    };

    updateActionPlan(payload, planId || actionPlan?.id, showMessage, navigate).then((data) => {
      navigate(
        generatePath(`${URLs.Salon}/${URLs.ActionPlan}`, {
          salonId,
          planId: planId || actionPlan?.id || data?.id,
          completed: null,
        }),
      );
    });
  };

  const rightButtons: IControlButton[] = [
    {
      label: t('back'),
      action: handleBackButton,
    },
    {
      label: t('next'),
      action: handleNextButton,
      variant: 'contained',
      disabled: !isMinSelected,
    },
  ];

  const getColumns: IColumn[] = useMemo(() => {
    return isIntlBenchmark
      ? KpiColumns
      : KpiColumns.filter((column) => column.id !== 'intlBenchmarkKpi');
  }, [isIntlBenchmark]);

  const rowsPrepare = useMemo(() => {
    if (salonKpiData === null) return [];

    const { kpi, benchmarkData, globalBenchmark } = salonKpiData;

    const rowsPrepared = [];

    for (const key in kpi) {
      const isSelected = salonSelectedKpi.includes(key);
      const isMaxSelected = salonSelectedKpi.length >= maxRowsSelected;
      const salonStatus = kpi[key].status;

      const kpiTextPrepare = (value: BenchmarkItemValues | string) => {
        if (typeof value === 'string') {
          return (KpiValues as { [key: string]: string })[value];
        }

        const maxValue = `${value.prefix ? value.prefix + ' ' : ''}${Math.ceil(value.maxValue)}${value.suffix || ''}`;

        return `${value.maxValue && maxValue}`;
      };

      const salonDataTextPrepare = (data: KpiItemValues) => {
        if (typeof data.value === 'string') {
          return (SalonKpiValues as { [key: string]: string })[data.value];
        }
        if (data.value === null) return 'N/A';

        return `${data.prefix ? data.prefix + ' ' : ''}${data.value}${data.suffix || ''}`;
      };

      rowsPrepared.push({
        kpi: KpiTitle[key as keyof typeof KpiTitle],
        avedaSalonData: salonDataTextPrepare(kpi[key]),
        globalBenchmarkKpi: kpiTextPrepare(globalBenchmark[key] as BenchmarkItemValues | string),
        intlBenchmarkKpi: kpiTextPrepare(benchmarkData[key] as BenchmarkItemValues | string),
        status: (
          <Stack>
            {kpi[key].value !== null && <KpiBadge result={salonStatus} />}
            <Typography>
              {kpi[key].value !== null ? KpiStatus[salonStatus as keyof typeof KpiStatus] : 'N/A'}
            </Typography>
            <CheckboxWrapper>
              <Checkbox
                checked={isSelected}
                disabled={kpi[key].value === null}
                onClick={() =>
                  !isSelected &&
                  isMaxSelected &&
                  showMessage(
                    `You cannot select more than 3 KPIs. To choose a different one, please uncheck one of the currently selected KPIs.`,
                    'success',
                  )
                }
                onChange={() => checkboxHandler(key, isMaxSelected)}
              />
            </CheckboxWrapper>
          </Stack>
        ),
        statusOrder: statusOrder[kpi[key].status] || 4,
      });
    }

    return rowsPrepared.sort((a, b) => a.statusOrder - b.statusOrder);
  }, [salonSelectedKpi, salonKpiData]);

  return (
    <>
      <ControlBar
        backButtonAction={() => navigate(generatePath(URLs.Salon, { salonId: salonId || null }))}
        title={<Typography variant="h5">{t('newPlan')}</Typography>}
        withKpiButton
      />
      <MainContainer>
        <Stack justifyContent="space-between" mb={2}>
          <Box>
            <Typography variant="h6">{t('results')}</Typography>
            <Typography variant="body2">{t('selectKpis')}</Typography>
          </Box>
        </Stack>
        <TableComponent rows={rowsPrepare} columns={getColumns} dataTestId="kpi-results-table" />
      </MainContainer>
      <ControlBar rightButtons={rightButtons} />
    </>
  );
};
export default KpiResults;
